import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`,
        { email },
        { headers: { "Accept-Language": lang || "fr" } }
      );
      setSent(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : t("generic.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen bg-[#050505] flex flex-col">
      <PublicHeader langTestId="forgot-lang" />
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
              <EnvelopeSimple size={26} weight="fill" className="text-amber-500" />
            </div>
            <h1 className="text-2xl font-light tracking-tight text-white">{t("forgot.title")}</h1>
            <p className="text-sm text-zinc-500 mt-2">{t("forgot.body")}</p>
          </div>

        {sent ? (
          <div data-testid="forgot-sent" className="bg-[#121212] border border-emerald-500/20 rounded-lg p-6 text-center space-y-4">
            <p className="text-sm text-emerald-400">{t("forgot.sent")}</p>
            <Button
              data-testid="forgot-back-btn"
              variant="outline"
              onClick={() => navigate("/login")}
              className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-11"
            >
              <ArrowLeft size={16} className="mr-2" />
              {t("forgot.back_login")}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#121212] border border-zinc-800 rounded-lg p-6 space-y-5">
            {error && (
              <div data-testid="forgot-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded px-3 py-2">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t("login.email")}</Label>
              <Input
                data-testid="forgot-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 focus:ring-amber-500/20 h-11"
              />
            </div>
            <Button
              data-testid="forgot-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 active:scale-[0.98] transition-all h-11"
            >
              {loading ? t("login.submitting") : t("forgot.submit")}
            </Button>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="block w-full text-center text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              {t("forgot.back_login")}
            </button>
          </form>
        )}
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
